import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CentralChatMessage } from './types';
import { DEFAULT_CHAT_ACCESS_MODE, type ChatAccessMode } from './chatAccessMode';

// A session is now scoped to a PAGE, not a tenant: its storage key is
// `${tenantScope}::${pageKey}` (see pageSessionKey.ts). `draft` and
// `lastReadMessageId` moved onto the session because they belong to the page's
// thread — its composer text and its own read cursor. Only `bubbleOpen` stays
// tenant-scoped in `uiByTenant`, so opening the rail survives navigation.
interface CentralChatSession {
  conversationId: string | null;
  messages: CentralChatMessage[];
  draft: string;
  lastReadMessageId: string | null;
  pendingChatAccessMode?: ChatAccessMode;
}

interface CentralChatUiState {
  bubbleOpen: boolean;
}

/** What page a conversation belongs to, so history rows can say which page. */
interface PageConversationIndex {
  latestConversationId: string | null;
  conversationIds: string[];
  label: string;
}

interface CentralChatStore {
  sessionsByTenant: Record<string, CentralChatSession>;
  uiByTenant: Record<string, CentralChatUiState>;
  pagesByTenant: Record<string, Record<string, PageConversationIndex>>;
  accessModeByConversation: Record<string, ChatAccessMode>;
  getSession: (scopeKey: string | null | undefined) => CentralChatSession;
  getUiState: (tenantScope: string | null | undefined) => CentralChatUiState;
  getPages: (tenantScope: string | null | undefined) => Record<string, PageConversationIndex>;
  setConversationId: (scopeKey: string, conversationId: string | null, tenantScope?: string) => void;
  getChatAccessMode: (tenantScope: string | null, scopeKey: string | null) => ChatAccessMode;
  setChatAccessMode: (tenantScope: string, scopeKey: string, mode: ChatAccessMode) => void;
  deleteChatAccessMode: (tenantScope: string, conversationId: string) => void;
  setBubbleOpen: (tenantScope: string, open: boolean) => void;
  setDraft: (scopeKey: string, draft: string) => void;
  markSessionRead: (scopeKey: string, messageId: string | null) => void;
  recordPageConversation: (
    tenantScope: string,
    pageKey: string,
    label: string,
    conversationId: string,
  ) => void;
  appendMessage: (scopeKey: string, message: CentralChatMessage) => void;
  updateMessage: (
    scopeKey: string,
    messageId: string,
    updater: (message: CentralChatMessage) => CentralChatMessage,
  ) => void;
  upsertMessage: (
    scopeKey: string,
    messageId: string,
    role: CentralChatMessage['role'],
    updater: (message: CentralChatMessage) => CentralChatMessage,
  ) => void;
  replaceMessages: (
    scopeKey: string,
    conversationId: string | null,
    messages: CentralChatMessage[],
  ) => void;
  resetSession: (scopeKey: string) => void;
}

const EMPTY_SESSION: CentralChatSession = {
  conversationId: null,
  messages: [],
  draft: '',
  lastReadMessageId: null,
  pendingChatAccessMode: DEFAULT_CHAT_ACCESS_MODE,
};

const EMPTY_UI_STATE: CentralChatUiState = {
  bubbleOpen: false,
};

const EMPTY_PAGES: Record<string, PageConversationIndex> = {};

/**
 * v1 keyed sessions by tenant scope and kept draft/lastRead in uiByTenant.
 * v2 keys sessions by PAGE. We cannot know which page an old tenant session
 * belonged to, so it is preserved under a legacy page key (never lost, never
 * crashing); it also stays reachable through the backend history list.
 */
export function migrateCentralChatStorage(
  persisted: unknown,
  version: number,
): Pick<CentralChatStore, 'sessionsByTenant' | 'uiByTenant' | 'pagesByTenant'> {
  const state = (persisted ?? {}) as {
    sessionsByTenant?: Record<
      string,
      { conversationId?: string | null; messages?: CentralChatMessage[] }
    >;
    uiByTenant?: Record<
      string,
      { bubbleOpen?: boolean; draft?: string; lastReadMessageId?: string | null }
    >;
    pagesByTenant?: Record<string, Record<string, PageConversationIndex>>;
  };
  if (version >= 2) {
    return {
      sessionsByTenant: state.sessionsByTenant as Record<string, CentralChatSession>,
      uiByTenant: state.uiByTenant as Record<string, CentralChatUiState>,
      pagesByTenant: state.pagesByTenant ?? {},
    };
  }
  const oldSessions = state.sessionsByTenant ?? {};
  const oldUi = state.uiByTenant ?? {};
  const sessionsByTenant: Record<string, CentralChatSession> = {};
  for (const [tenantScope, legacy] of Object.entries(oldSessions)) {
    sessionsByTenant[`${tenantScope}::__legacy__`] = {
      conversationId: legacy.conversationId ?? null,
      messages: legacy.messages ?? [],
      draft: oldUi[tenantScope]?.draft ?? '',
      lastReadMessageId: oldUi[tenantScope]?.lastReadMessageId ?? null,
    };
  }
  const uiByTenant: Record<string, CentralChatUiState> = {};
  for (const [tenantScope, ui] of Object.entries(oldUi)) {
    uiByTenant[tenantScope] = { bubbleOpen: ui.bubbleOpen ?? false };
  }
  return { sessionsByTenant, uiByTenant, pagesByTenant: {} };
}

export const useCentralChatStore = create<CentralChatStore>()(
  persist(
    (set, get) => ({
      sessionsByTenant: {},
      uiByTenant: {},
      pagesByTenant: {},
      accessModeByConversation: {},

      getChatAccessMode: (tenantScope, scopeKey) => {
        if (!tenantScope || !scopeKey) return DEFAULT_CHAT_ACCESS_MODE;
        const session = get().sessionsByTenant[scopeKey] ?? EMPTY_SESSION;
        return session.conversationId
          ? get().accessModeByConversation[`${tenantScope}::${session.conversationId}`] ?? DEFAULT_CHAT_ACCESS_MODE
          : session.pendingChatAccessMode ?? DEFAULT_CHAT_ACCESS_MODE;
      },

      setChatAccessMode: (tenantScope, scopeKey, mode) =>
        set((state) => {
          const session = state.sessionsByTenant[scopeKey] ?? EMPTY_SESSION;
          if (session.conversationId) {
            return {
              accessModeByConversation: {
                ...state.accessModeByConversation,
                [`${tenantScope}::${session.conversationId}`]: mode,
              },
            };
          }
          return {
            sessionsByTenant: {
              ...state.sessionsByTenant,
              [scopeKey]: { ...session, pendingChatAccessMode: mode },
            },
          };
        }),

      deleteChatAccessMode: (tenantScope, conversationId) =>
        set((state) => {
          const modes = { ...state.accessModeByConversation };
          delete modes[`${tenantScope}::${conversationId}`];
          return { accessModeByConversation: modes };
        }),

      getSession: (scopeKey) => {
        if (!scopeKey) return EMPTY_SESSION;
        return get().sessionsByTenant[scopeKey] ?? EMPTY_SESSION;
      },

      getUiState: (tenantScope) => {
        if (!tenantScope) return EMPTY_UI_STATE;
        return get().uiByTenant[tenantScope] ?? EMPTY_UI_STATE;
      },

      getPages: (tenantScope) => {
        if (!tenantScope) return EMPTY_PAGES;
        return get().pagesByTenant[tenantScope] ?? EMPTY_PAGES;
      },

      setConversationId: (scopeKey, conversationId, tenantScope) =>
        set((state) => {
          const current = state.sessionsByTenant[scopeKey] ?? EMPTY_SESSION;
          if (current.conversationId === conversationId) return state;

          const modeKey = tenantScope && conversationId ? `${tenantScope}::${conversationId}` : null;

          return {
            sessionsByTenant: {
              ...state.sessionsByTenant,
              [scopeKey]: { ...current, conversationId, pendingChatAccessMode: DEFAULT_CHAT_ACCESS_MODE },
            },
            ...(modeKey && !current.conversationId && !state.accessModeByConversation[modeKey]
              ? {
                  accessModeByConversation: {
                    ...state.accessModeByConversation,
                    [modeKey]: current.pendingChatAccessMode ?? DEFAULT_CHAT_ACCESS_MODE,
                  },
                }
              : {}),
          };
        }),

      setBubbleOpen: (tenantScope, open) =>
        set((state) => {
          const current = state.uiByTenant[tenantScope] ?? EMPTY_UI_STATE;
          if (current.bubbleOpen === open) return state;

          return {
            uiByTenant: {
              ...state.uiByTenant,
              [tenantScope]: { ...current, bubbleOpen: open },
            },
          };
        }),

      setDraft: (scopeKey, draft) =>
        set((state) => {
          const current = state.sessionsByTenant[scopeKey] ?? EMPTY_SESSION;
          if (current.draft === draft) return state;

          return {
            sessionsByTenant: {
              ...state.sessionsByTenant,
              [scopeKey]: { ...current, draft },
            },
          };
        }),

      markSessionRead: (scopeKey, messageId) =>
        set((state) => {
          const current = state.sessionsByTenant[scopeKey] ?? EMPTY_SESSION;
          if (current.lastReadMessageId === messageId) return state;

          return {
            sessionsByTenant: {
              ...state.sessionsByTenant,
              [scopeKey]: { ...current, lastReadMessageId: messageId },
            },
          };
        }),

      recordPageConversation: (tenantScope, pageKey, label, conversationId) =>
        set((state) => {
          const pages = state.pagesByTenant[tenantScope] ?? {};
          const current = pages[pageKey];
          if (
            current &&
            current.latestConversationId === conversationId &&
            current.label === label &&
            current.conversationIds.includes(conversationId)
          ) {
            return state;
          }
          const conversationIds = current?.conversationIds.includes(conversationId)
            ? current.conversationIds
            : [...(current?.conversationIds ?? []), conversationId];
          return {
            pagesByTenant: {
              ...state.pagesByTenant,
              [tenantScope]: {
                ...pages,
                [pageKey]: { latestConversationId: conversationId, conversationIds, label },
              },
            },
          };
        }),

      appendMessage: (scopeKey, message) =>
        set((state) => {
          const current = state.sessionsByTenant[scopeKey] ?? EMPTY_SESSION;
          return {
            sessionsByTenant: {
              ...state.sessionsByTenant,
              [scopeKey]: {
                ...current,
                messages: [...current.messages, message],
              },
            },
          };
        }),

      updateMessage: (scopeKey, messageId, updater) =>
        set((state) => {
          const current = state.sessionsByTenant[scopeKey] ?? EMPTY_SESSION;
          return {
            sessionsByTenant: {
              ...state.sessionsByTenant,
              [scopeKey]: {
                ...current,
                messages: current.messages.map((message) =>
                  message.id === messageId ? updater(message) : message,
                ),
              },
            },
          };
        }),

      // Like updateMessage, but if the target message is gone (e.g. a cross-tab
      // rehydrate replaced the list mid-stream), re-add it so the streamed reply
      // still lands instead of silently vanishing.
      upsertMessage: (scopeKey, messageId, role, updater) =>
        set((state) => {
          const current = state.sessionsByTenant[scopeKey] ?? EMPTY_SESSION;
          const exists = current.messages.some((message) => message.id === messageId);
          const messages = exists
            ? current.messages.map((message) =>
                message.id === messageId ? updater(message) : message,
              )
            : [
                ...current.messages,
                updater({
                  id: messageId,
                  role,
                  content: '',
                  createdAt: new Date().toISOString(),
                }),
              ];
          return {
            sessionsByTenant: {
              ...state.sessionsByTenant,
              [scopeKey]: { ...current, messages },
            },
          };
        }),

      replaceMessages: (scopeKey, conversationId, messages) =>
        set((state) => {
          const current = state.sessionsByTenant[scopeKey] ?? EMPTY_SESSION;
          return {
            sessionsByTenant: {
              ...state.sessionsByTenant,
              [scopeKey]: { ...current, conversationId, messages },
            },
          };
        }),

      resetSession: (scopeKey) =>
        set((state) => ({
          sessionsByTenant: {
            ...state.sessionsByTenant,
            [scopeKey]: EMPTY_SESSION,
          },
        })),
    }),
    {
      name: 'central-chat-storage',
      version: 2,
      migrate: (persisted, version) => migrateCentralChatStorage(persisted, version),
    },
  ),
);

export function centralChatScopeKey(
  tenantId: string | null | undefined,
  userId?: string | null,
): string | null {
  if (!tenantId) return null;
  return userId ? `${userId}::${tenantId}` : tenantId;
}

export function createCentralChatMessage(
  role: CentralChatMessage['role'],
  content: string,
  overrides: Partial<CentralChatMessage> = {},
): CentralChatMessage {
  return {
    id: typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${role}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    role,
    content,
    createdAt: new Date().toISOString(),
    ...overrides,
  };
}
